import { useState } from 'react';
import { supabase } from '@/lib/supabase';

export interface CloudflareAlias {
  id: string;
  alias: string;
  destinations: string[];
  enabled: boolean;
  name?: string;
}

export interface CloudflareStaffEntry {
  user_id: string;
  nom: string;
  prenom: string;
  email: string | null;
  email_2fa: string | null;
  table: string;
  role: string;
  icon: string;
}

interface CloudflareLinkModalProps {
  entry: CloudflareStaffEntry;
  aliases: CloudflareAlias[];
  onClose: () => void;
  onLinked: (alias: string) => void;
}

const DOMAIN = 'cliniquesaintefamille.com';

function slugify(str: string): string {
  return str
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '.')
    .replace(/^\.+|\.+$/g, '');
}

export default function CloudflareLinkModal({ entry, aliases, onClose, onLinked }: CloudflareLinkModalProps) {
  const [tab, setTab] = useState<'create' | 'assign'>(() =>
    aliases.length > 0 ? 'assign' : 'create'
  );
  const [personalEmail, setPersonalEmail] = useState('');
  const [alias, setAlias] = useState(() => {
    const base = slugify(`${entry.prenom} ${entry.nom}`) || 'membre';
    return `${base}@${DOMAIN}`;
  });
  const [selectedAlias, setSelectedAlias] = useState('');
  const [aliasSearch, setAliasSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [done, setDone] = useState(false);

  const handleCreate = async () => {
    setLoading(true);
    setErrorMsg('');
    setSuccessMsg('');
    const dest = personalEmail.trim();
    const al = alias.trim();

    if (!dest) {
      setErrorMsg("Renseignez l'email personnel.");
      setLoading(false);
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(dest)) {
      setErrorMsg('Email personnel invalide.');
      setLoading(false);
      return;
    }
    if (!al.endsWith('@' + DOMAIN)) {
      setErrorMsg(`L'alias doit se terminer par @${DOMAIN}.`);
      setLoading(false);
      return;
    }

    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;
      if (!token) throw new Error('Non authentifié');

      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/manage-cloudflare-email-routing`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ action: 'create', alias: al, destination: dest }),
        }
      );
      const data = await response.json();

      if (data.status === 'exists') {
        setErrorMsg(`L'alias ${al} existe déjà dans Cloudflare. Utilisez l'onglet "Alias existant".`);
        setLoading(false);
        return;
      }
      if (data.status !== 'ok') {
        const parts: string[] = [];
        if (data.reason) parts.push(data.reason);
        if (data.detail) parts.push(`Cloudflare : ${data.detail}`);
        if (data.hint) parts.push(data.hint);
        setErrorMsg(parts.join(' ') || data.error || 'Erreur lors de la création.');
        setLoading(false);
        return;
      }

      onLinked(al);
      setSuccessMsg(data.message || `Alias ${al} créé et lié à ${dest}.`);
      setDone(true);
    } catch (e: any) {
      setErrorMsg(e.message || 'Erreur réseau');
    } finally {
      setLoading(false);
    }
  };

  const handleAssign = () => {
    if (!selectedAlias) {
      setErrorMsg('Sélectionnez un alias.');
      return;
    }
    onLinked(selectedAlias);
    onClose();
  };

  const filteredAliases = aliases.filter((a) => {
    const q = aliasSearch.trim().toLowerCase();
    if (!q) return true;
    return (
      a.alias.toLowerCase().includes(q) ||
      (a.destinations || []).some((d) => d.toLowerCase().includes(q))
    );
  });

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-xl w-full max-w-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b-2 border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center">
              <i className="ri-cloud-line text-lg text-orange-600"></i>
            </div>
            <div>
              <h3 className="font-bold text-clinic-blue-dark text-base">Lier un email 2FA</h3>
              <p className="text-xs text-gray-500">
                {entry.prenom} {entry.nom} — {entry.role}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 flex items-center justify-center cursor-pointer"
            title="Fermer"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        <div className="p-6">
          {done ? (
            <div className="text-center py-6">
              <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-check-line text-2xl text-green-600"></i>
              </div>
              <p className="text-sm text-gray-700 mb-1 font-semibold">Alias lié avec succès</p>
              <p className="text-xs text-gray-500 mb-5">{successMsg}</p>
              <button
                onClick={onClose}
                className="px-5 py-2 bg-clinic-green text-white rounded-lg hover:bg-clinic-green-dark transition-colors whitespace-nowrap text-sm cursor-pointer"
              >
                Terminer
              </button>
            </div>
          ) : (
            <>
              <div className="flex p-1 bg-gray-100 rounded-full mb-5">
                <button
                  onClick={() => setTab('create')}
                  className={`flex-1 px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                    tab === 'create' ? 'bg-white text-clinic-blue-dark shadow-sm' : 'text-gray-500'
                  }`}
                >
                  Créer un alias
                </button>
                <button
                  onClick={() => setTab('assign')}
                  className={`flex-1 px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                    tab === 'assign' ? 'bg-white text-clinic-blue-dark shadow-sm' : 'text-gray-500'
                  }`}
                >
                  Alias existant
                  {aliases.length > 0 && (
                    <span
                      className={`ml-1.5 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-xs font-semibold ${
                        tab === 'assign' ? 'bg-clinic-green text-white' : 'bg-gray-200 text-gray-600'
                      }`}
                    >
                      {aliases.length}
                    </span>
                  )}
                </button>
              </div>

              {errorMsg && (
                <div className="mb-4 p-3 bg-red-50 border-2 border-red-200 text-red-600 rounded-xl text-sm flex items-start">
                  <i className="ri-error-warning-line mr-2 mt-0.5 flex-shrink-0"></i>
                  <span>{errorMsg}</span>
                </div>
              )}

              {tab === 'create' ? (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Email personnel du membre
                    </label>
                    <input
                      type="email"
                      value={personalEmail}
                      onChange={(e) => setPersonalEmail(e.target.value)}
                      placeholder="jean.dupont@gmail.com"
                      className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-lg focus:outline-none focus:border-clinic-blue/40 text-sm"
                    />
                    <p className="text-xs text-gray-500 mt-1.5 flex items-start gap-1">
                      <i className="ri-information-line mt-0.5 flex-shrink-0"></i>
                      <span>
                        Cloudflare enverra un email de vérification à cette adresse. Le membre devra cliquer
                        dessus pour activer la redirection.
                      </span>
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Alias (adresse du domaine)
                    </label>
                    <input
                      type="text"
                      value={alias}
                      onChange={(e) => setAlias(e.target.value)}
                      className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-lg focus:outline-none focus:border-clinic-blue/40 text-sm"
                    />
                    <p className="text-xs text-gray-500 mt-1.5">Généré automatiquement, modifiable.</p>
                  </div>
                  <button
                    onClick={handleCreate}
                    disabled={loading}
                    className="w-full py-2.5 bg-clinic-green text-white rounded-lg hover:bg-clinic-green-dark transition-colors whitespace-nowrap text-sm font-medium flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50"
                  >
                    {loading ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-cloud-line"></i>}
                    Créer l'alias + la redirection
                  </button>
                </div>
              ) : (
                <div>
                  <div className="relative mb-3">
                    <input
                      type="text"
                      value={aliasSearch}
                      onChange={(e) => setAliasSearch(e.target.value)}
                      placeholder="Rechercher un alias ou une destination..."
                      className="w-full pl-9 pr-3 py-2.5 bg-white border-2 border-gray-200 rounded-lg focus:outline-none focus:border-clinic-blue/40 text-sm"
                    />
                    <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
                    {aliasSearch && (
                      <button
                        onClick={() => setAliasSearch('')}
                        className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer"
                        title="Effacer la recherche"
                      >
                        <i className="ri-close-circle-fill text-sm"></i>
                      </button>
                    )}
                  </div>

                  {aliases.length === 0 ? (
                    <div className="text-center py-6 text-gray-400">
                      <i className="ri-cloud-off-line text-3xl block mb-2"></i>
                      <p className="text-sm">Aucun alias trouvé dans Cloudflare.</p>
                      <button
                        onClick={() => setTab('create')}
                        className="mt-2 text-clinic-blue text-sm hover:underline cursor-pointer"
                      >
                        Créer un nouvel alias
                      </button>
                    </div>
                  ) : filteredAliases.length === 0 ? (
                    <div className="text-center py-6 text-gray-400">
                      <i className="ri-search-eye-line text-3xl block mb-2"></i>
                      <p className="text-sm">Aucun alias ne correspond à votre recherche.</p>
                    </div>
                  ) : (
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {filteredAliases.map((a) => (
                        <button
                          key={a.id}
                          onClick={() => setSelectedAlias(a.alias)}
                          className={`w-full flex items-center gap-3 p-3 rounded-xl border-2 text-left transition-colors cursor-pointer ${
                            selectedAlias === a.alias
                              ? 'border-clinic-green bg-green-50'
                              : 'border-gray-100 hover:border-gray-200'
                          }`}
                        >
                          <div
                            className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                              selectedAlias === a.alias
                                ? 'bg-clinic-green text-white'
                                : 'bg-gray-100 text-gray-500'
                            }`}
                          >
                            <i className="ri-mail-line text-sm"></i>
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium text-gray-800 truncate">{a.alias}</p>
                            <p className="text-xs text-gray-500 truncate">
                              → {(a.destinations || []).join(', ') || 'aucune destination'}
                            </p>
                          </div>
                          {!a.enabled && <span className="text-xs text-gray-400 whitespace-nowrap">inactif</span>}
                        </button>
                      ))}
                    </div>
                  )}
                  <button
                    onClick={handleAssign}
                    disabled={!selectedAlias}
                    className="w-full mt-4 py-2.5 bg-clinic-green text-white rounded-lg hover:bg-clinic-green-dark transition-colors whitespace-nowrap text-sm font-medium flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50"
                  >
                    <i className="ri-link"></i>
                    Affecter cet alias à {entry.prenom}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}